import React, { useEffect, useState } from 'react';
import { RefActiveParticipantsWithAllPlacements, refreshAllDatasets } from '../hooks/DatasetHooks.js';
import * as libCon from '../community-hats-js-library/Constants.js';
import * as locCon from '../LocalConstants.js';
import { Button, Space, Table } from 'antd';
import { Typography } from 'antd';
import {
  getColorOfExpiry,
  getColorOfLastUpload,
  getTextOfExpiry,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
} from '../community-hats-js-library/utils/generalFunctions.js';
import { formatDistanceShort } from '../community-hats-js-library/utils/dateFunctions.js';
import { SymbolForStatus } from '../community-hats-js-library/utils/devicesStatusFunctions.js';
import { ArrowRightOutlined } from '@ant-design/icons';
import { setValue } from '../store/actions/generalStoreActions.js';
import { RefSetCurrentPage } from '../hooks/NavigationHooks.js';
import LoadingDatasets from '../components/LoadingDatasets.js';
import { RefStoredValue } from '../hooks/GeneralStoreHooks.js';
import LocationPicker from '../components/LocationPicker.js';
import { RefContextLanguage } from '../providers/LanguageProvider';
import { tMessage } from '../utils/translationFunctions';

const { Title } = Typography;

const getGeneralColumns = (i18n) => [
  {
    title: <p>{i18n.t('collectionStatus')}</p>,
    dataIndex: libCon.AT_COLLECTION_STATUS,
    key: 1,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.AT_COLLECTION_STATUS].localeCompare(b[libCon.AT_COLLECTION_STATUS]),
    render: (val, p) => (
      <SymbolForStatus translation={(v) => tMessage(i18n, v)} status={p[libCon.AT_COLLECTION_STATUS]} message={p[libCon.AT_COLLECTION_MESSAGE]} />
    ),
  },
  {
    title: <p>{i18n.t('area')}</p>,
    dataIndex: libCon.ATF_AREA,
    key: 1,
    align: 'center',
    width: 100,
  },
  {
    title: i18n.t('names'),
    dataIndex: libCon.ATF_NAME,
    key: 2,
    align: 'center',
    width: 100,
    fixed: 'left',
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {record[libCon.ATF_NAME] + ' ' + record[libCon.ATF_LAST_NAME]}
      </p>
    ),
  },
  {
    title: i18n.t('sewaId'),
    dataIndex: libCon.ATF_SEWA_ID,
    key: 3,
    align: 'center',
    width: 100,
    render: (val, record) => (
      <p style={{ fontSize: 12, textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val}
      </p>
    ),
  },
  {
    title: i18n.t('address'),
    dataIndex: libCon.ATF_ADDRESS,
    key: 4,
    align: 'center',
    width: 100,
    render: (val, record) => (
      <p style={{ fontSize: 12, textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {val}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('nextCollection')}</p>,
    dataIndex: libCon.ATF_DAYS_UNTIL_EXPIRE,
    key: 5,
    align: 'center',
    width: 100,
    defaultSortOrder: 'ascend',
    sorter: (a, b) => a[libCon.ATF_DAYS_UNTIL_EXPIRE] - b[libCon.ATF_DAYS_UNTIL_EXPIRE],
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfExpiry(record[libCon.ATF_DAYS_UNTIL_EXPIRE]) }}>
        {getTextOfExpiry(val)}
      </p>
    ),
  },
  {
    title: <p>{i18n.t('lastCollection')}</p>,
    dataIndex: libCon.ATF_LATEST_COLLECTION_DATE,
    key: 6,
    align: 'center',
    width: 100,
    sorter: (a, b) => a[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT] - b[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT],
    render: (val, record) => (
      <p style={{ textAlign: 'center', color: getColorOfLastUpload(record[libCon.ATF_DAYS_SINCE_LAST_HOUSE_VISIT]) }}>
        {formatDistanceShort(val)}
      </p>
    ),
  },
];

function DashboardParticipantsToBeUnenrolled() {
  // Navigation
  const setCurrentPage = RefSetCurrentPage();

  // Location
  const location = RefStoredValue(locCon.PROJECT_LOCATION);

  // Participants
  const [status, participants] = RefActiveParticipantsWithAllPlacements();

  const [participantArray, setParticipantArray] = useState([]);

  // Status
  const [isTableReady, setIsTableReady] = useState(false);

  useEffect(() => {
    setIsTableReady(status !== libCon.STATUS_NOT_STARTED && status !== libCon.LOADING && participants !== null);
  }, [status, participants]);

  useEffect(() => {
    if (participants !== null) {
      let parArray = Object.values(participants);

      // Filter Participants that need to be unenroll
      parArray = parArray.filter(
        (ob) =>
          !isNullOrUndefinedOrEmpty(ob[libCon.ATF_PARTICIPATION_COMPLETE_ISSUES]) &&
          ob[libCon.ATF_PARTICIPATION_COMPLETE_ISSUES] > 0
      );

      // Filter Location
      if (!isNullOrUndefined(location) && location !== libCon.ATF_ALL)
        parArray = parArray.filter(
          (ob) =>
            libCon.ATF_AREA in ob && ob[libCon.ATF_AREA].map((s) => s.toUpperCase()).includes(location.toUpperCase())
        );

      setParticipantArray(parArray);
    }
  }, [participants, location]);

  const userType = RefStoredValue(locCon.USER_TYPE);

  const i18n = RefContextLanguage();

  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const goToParticipant = (id) => {
      setValue(locCon.SELECTED_PARTICIPANT, id);
      setCurrentPage(locCon.PAGE_DASHBOARD_SINGLE_COLLECTION_STATUS);
    };

    let localColumns = [
      ...getGeneralColumns(i18n),
      {
        title: i18n.t('goTo'),
        dataIndex: libCon.ID,
        key: 14,
        align: 'center',
        width: 80,
        render: (val) => (
          <Button type="primary" onClick={() => goToParticipant(val)}>
            <ArrowRightOutlined />
          </Button>
        ),
      },
    ];

    if (!isNullOrUndefined(location) && location !== libCon.ATF_ALL) {
      localColumns = localColumns.filter((col) => col.dataIndex !== libCon.ATF_AREA);
    }

    const sensitiveCols = [libCon.ATF_NAME, libCon.ATF_ADDRESS, libCon.ATF_PHONE_NUMBER];

    if (userType !== locCon.USER_TYPE_SEWA)
      localColumns = localColumns.filter((col) => !sensitiveCols.includes(col.dataIndex));

    setColumns(localColumns);
  }, [userType, setCurrentPage, location, i18n]);

  return (
    <div className="mainContainer">
      <Title level={2}>{i18n.t('participantsReadyToBeUnenrolled')}</Title>
      <Button type="primary" onClick={() => refreshAllDatasets()}>
        {i18n.t('refresh')}
      </Button>

      <Space direction="vertical" style={{ marginBottom: '10px' }}>
        <LocationPicker />
      </Space>

      <Space direction="vertical">
        <Title
          level={3}
        >{`${i18n.t('totalParticipantsReadyForUnenrollment')}: ${!isTableReady ? '---' : participantArray.length}`}</Title>
      </Space>

      <div style={{ maxWidth: '98vw', marginTop: '20px' }}>
        {!isTableReady ? (
          <LoadingDatasets />
        ) : status === libCon.ERROR ? (
          <div>{i18n.t('error')}</div>
        ) : participantArray.length === 0 ? (
          <Title level={4}>{i18n.t('noParticipantsReadyForUnenrollment')}</Title>
        ) : (
          <Table
            dataSource={participantArray}
            columns={columns}
            size="small"
            scroll={{ x: 'max-content', y: window.innerHeight * 0.65 }}
            pagination={{ pageSize: 50 }}
          />
        )}
      </div>
    </div>
  );
}

export default DashboardParticipantsToBeUnenrolled;
